import posthog from "posthog-js";

type PostHogProperties = Record<string, string | number | boolean | string[] | undefined>;

let initialized = false;
const seenEvents = new Set<string>();

const getKey = () => process.env.NEXT_PUBLIC_POSTHOG_KEY?.trim();

const getHost = () => process.env.NEXT_PUBLIC_POSTHOG_HOST?.trim();

const storageKey = (key: string) => `sr_posthog_event:${key}`;

export const posthogAnalytics = {
  isConfigured: () => typeof window !== "undefined" && Boolean(getKey()),

  init: () => {
    const key = getKey();
    const host = getHost();
    if (typeof window === "undefined" || initialized) return;

    if (!key) {
      if (process.env.NODE_ENV !== "production") {
        throw new Error(
          "NEXT_PUBLIC_POSTHOG_KEY variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once NEXT_PUBLIC_POSTHOG_KEY is configured"
        );
      }
      return;
    }

    if (!host) {
      if (process.env.NODE_ENV !== "production") {
        throw new Error(
          "NEXT_PUBLIC_POSTHOG_HOST variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once NEXT_PUBLIC_POSTHOG_HOST is configured"
        );
      }
      return;
    }

    posthog.init(key, {
      api_host: host,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      persistence: "localStorage+cookie",
      respect_dnt: true,
    });
    initialized = true;
  },

  pageView: (pathname: string) => {
    if (!initialized) return;
    posthog.capture("$pageview", {
      route: pathname,
      page_title: document.title,
      $current_url: window.location.href,
    });
  },

  capture: (eventName: string, properties: PostHogProperties, eventId?: string) => {
    if (!initialized) return;
    posthog.capture(eventName, {
      ...properties,
      ...(eventId ? { $insert_id: eventId } : {}),
    });
  },

  identify: (distinctId: string, properties: PostHogProperties) => {
    if (!initialized) return;
    posthog.identify(distinctId, properties);
  },

  reset: () => {
    if (!initialized) return;
    posthog.reset();
  },

  once: (key: string, callback: () => void) => {
    if (seenEvents.has(key)) return;
    try {
      if (sessionStorage.getItem(storageKey(key))) {
        seenEvents.add(key);
        return;
      }
      sessionStorage.setItem(storageKey(key), "1");
    } catch {
      // Analytics must never block booking when storage is unavailable.
    }
    seenEvents.add(key);
    callback();
  },
};
